/**
 * ActionMenuTab Component
 * Action menu for dashboard tabs with dropdown options
 * Migrated from legacy ActionMenuTab.js
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import RenderInBody from '@/components/utils/RenderInBody';
import { Modal } from '@/components/utils/Modal';
import { dashboardApi } from '@/services/dashboardApi';
import type { ActionMenuTabProps, Dashboard } from './types';
import {
  ConfigCapacityTracker,
  ConfigSpModal,
  DeleteDashboardModal,
  EditDashboardModal,
  EditDashboardColumn,
  DuplicateModal,
  ImportFromJira,
} from '@components/Dashboard/TabsNavigation/modals';
import './ActionMenuTab.css';
import threeDotsIcon from '@/assets/icons/three-dots.svg';
import { dashboardService } from '@/services/dashboardService';
import { checkEditPermissions, checkPTOApproverPermissions } from '../utils/CorgeeHelper';
import { useToast } from '@/components/ui/Toast';

// Note: ImportFromJira and EditProjectColumn are external components
// They should be imported when available in the project
// import ImportFromJira from "@devbot/importfromjira";
// import EditProjectColumn from "@devbot/editprojectcolumn";

const ActionMenuTab: React.FC<ActionMenuTabProps> = ({
  typeMenu,
  dashboard,
  config,
  api: _api,
  functions,
  canEdit: _canEditProp,
  isAdmin,
  dashboards,
  notPinedDashboards: initialNotPinedDashboards,
  openTabDashboard,
  ifjMoveToModal,
  onToggleTab,
  setPinedDashboards,
  setNotPinedDashboards: setNotPinDashboard,
  activeDashboard,
  tabsToShow,
  onSelectDashboard,
  onDashboardUpdated,
}) =>
{
  const [isOpen, setIsOpen] = useState(false);
  const [notPinedDashboards, setNotPinedDashboards] = useState<Dashboard[]>(
    initialNotPinedDashboards || [],
  );
  const [notPinedDashboardsFiltered, setNotPinedDashboardsFiltered] = useState<Dashboard[]>(
    initialNotPinedDashboards || [],
  );
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [currentDashboard, setCurrentDashboard] = useState<Dashboard | null>(null);
  const { success: toastSuccess } = useToast();
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, right: 0 });
  const [menuMaxHeight, setMenuMaxHeight] = useState<number | undefined>(undefined);
  const menuRef = useRef<HTMLUListElement>(null);
  const [importJiraSelectedDashboard, setImportJiraSelectedDashboard] = useState<Dashboard | null>(
    null,
  );
  const [configDashboard, setConfigDashboard] = useState<any>(null);

  const handleReloadDashboard = useCallback(() =>
  {
    if (currentDashboard && currentDashboard.DashboardId === activeDashboard)
    {
      onSelectDashboard?.(currentDashboard);
    }
  }, [currentDashboard, activeDashboard, onSelectDashboard]);

  //Edit Modal
  const [openEditModal, setOpenEditModal] = useState(false);
  //Config SP Modal
  const [openConfigSpModal, setOpenConfigSpModal] = useState(false);
  // Capacity Tracker
  const [openCapacityTracker, setOpenCapacityTracker] = useState(false);
  // Delete Tab
  const [openDeleteTabModal, setOpenDeleteTabModal] = useState(false);
  // Edit Dashboard Column
  const [_editProjectColumnModalOpen, setEditProjectColumnModalOpen] = useState(false);
  // Duplicate Modal
  const [duplicateModal, setDuplicateModal] = useState(false);
  // Import from Jira Modal
  const [importJiraModalOpen, setImportJiraModalOpen] = useState(false);

  const actionMenuRef = useRef<HTMLDivElement>(null);
  const subMenuRef = useRef<HTMLUListElement>(null);
  // Tracked by id so the submenu follows list refreshes
  const [subMenuDashboardId, setSubMenuDashboardId] = useState<number | null>(null);
  const [subMenuPosition, setSubMenuPosition] = useState<{ top: number; left: number } | null>(null);
  const [showCopyNotification, setShowCopyNotification] = useState(false);
  const [copyNotificationFading, setCopyNotificationFading] = useState(false);
  const copyNotificationTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  // Show the "copied" notification, then fade it out and unmount after ~2s.
  const notifyLinkCopied = useCallback(() =>
  {
    copyNotificationTimeoutsRef.current.forEach(clearTimeout);
    copyNotificationTimeoutsRef.current = [];

    setCopyNotificationFading(false);
    setShowCopyNotification(true);

    // Start the fade-out slightly before unmount so the exit animation is visible.
    const fadeTimeout = setTimeout(() => setCopyNotificationFading(true), 1700);
    const hideTimeout = setTimeout(() =>
    {
      setShowCopyNotification(false);
      setCopyNotificationFading(false);
    }, 2000);
    copyNotificationTimeoutsRef.current = [fadeTimeout, hideTimeout];
  }, []);

  // Clean up any pending timeouts on unmount.
  useEffect(() =>
  {
    return () =>
    {
      copyNotificationTimeoutsRef.current.forEach(clearTimeout);
    };
  }, []);

  const isPTOApprover = checkPTOApproverPermissions();
  // User-level edit permission (legacy authService.CanEdit) — gates the menu options.
  // Renaming a dashboard is a separate, stricter check: creator or admin only.
  const canEdit = checkEditPermissions();

  // Update notPinedDashboards when prop changes
  useEffect(() =>
  {
    if (initialNotPinedDashboards)
    {
      setNotPinedDashboards(initialNotPinedDashboards);
      setNotPinedDashboardsFiltered([...initialNotPinedDashboards]);
    }
  }, [initialNotPinedDashboards]);

  // Adjust menu position if it overflows the viewport
  useEffect(() =>
  {
    if (!isOpen) return;

    setMenuMaxHeight(undefined);

    requestAnimationFrame(() =>
    {
      requestAnimationFrame(() =>
      {
        const menu = menuRef.current;
        if (!menu) return;

        const menuRect = menu.getBoundingClientRect();
        const padding = 10;
        const currentTop = parseFloat(menu.style.top) || 0;
        const currentLeft = parseFloat(menu.style.left) || 0;
        const currentRight = parseFloat(menu.style.right) || 0;

        let adjustedTop = currentTop;
        let adjustedLeft = currentLeft;
        let adjustedRight = currentRight;
        let maxHeight: number | undefined = undefined;

        if (menuRect.bottom > window.innerHeight - padding)
        {
          const spaceBelow = window.innerHeight - currentTop - padding;
          const spaceAbove = currentTop - padding;

          if (spaceAbove > spaceBelow && spaceAbove > 100)
          {
            adjustedTop = Math.max(padding, currentTop - menuRect.height - padding);
            if (adjustedTop < padding)
            {
              adjustedTop = padding;
              maxHeight = spaceAbove - padding * 2;
            }
          }
          else
          {
            maxHeight = spaceBelow - padding;
          }
        }

        if (typeMenu === '1')
        {
          if (menuRect.right > window.innerWidth - padding)
          {
            adjustedLeft = window.innerWidth - menuRect.width - padding;
            adjustedLeft = Math.max(padding, adjustedLeft);
          }
        }
        else if (typeMenu === '2')
        {
          if (menuRect.left < padding)
          {
            adjustedRight = window.innerWidth - menuRect.width - padding;
            adjustedRight = Math.max(padding, adjustedRight);
          }
        }

        const needsUpdate =
          adjustedTop !== currentTop ||
          adjustedLeft !== currentLeft ||
          adjustedRight !== currentRight ||
          maxHeight !== undefined;

        if (needsUpdate)
        {
          setMenuPosition({
            top: adjustedTop,
            left: adjustedLeft,
            right: adjustedRight,
          });
          if (maxHeight !== undefined)
          {
            setMenuMaxHeight(maxHeight);
          }
        }
      });
    });
  }, [isOpen, typeMenu]);

  // Handle scroll to close menu
  useEffect(() =>
  {
    const handleScroll = () =>
    {
      if (isOpen)
      {
        setIsOpen(false);
        setSubMenuDashboardId(null);
        setSubMenuPosition(null);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isOpen]);

  const getUrl = useCallback(
    (dash: Dashboard): string =>
    {
      try
      {
        const base = (window.location.origin + import.meta.env.BASE_URL).replace(/\/$/, '');
        if (config.projectId && config.projectId > 0)
        {
          return `${base}/ProjectDetails/${config.projectId}?ProjectDashboardId=${dash.DashboardId}`;
        }
        else if (config.configValueId)
        {
          return `${base}/Dashboard/${dash.DashboardId}/${config.configValueId}`;
        }
      }
      catch (error)
      {
        console.error('Error generating URL:', error);
      }
      return '';
    },
    [config],
  );

  const getDateActual = useCallback((): string =>
  {
    const date = new Date();
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  }, []);

  const closeMenu = useCallback(() =>
  {
    setShowConfirmationModal(false);
    setIsOpen(false);
    setSubMenuDashboardId(null);
    setSubMenuPosition(null);
    setNotPinedDashboardsFiltered([...(initialNotPinedDashboards || [])]);
  }, [initialNotPinedDashboards]);

  const onOpenMenu = useCallback(
    (e: React.MouseEvent) =>
    {
      e.preventDefault();
      const elem = e.currentTarget as HTMLElement;

      if (!isOpen)
      {
        // Calculate position immediately from the clicked element
        const rect = elem.getBoundingClientRect();
        setMenuPosition({
          top: rect.bottom + 2,
          left: rect.left,
          right: window.innerWidth - rect.right,
        });
        setIsOpen(true);
      }
      else
      {
        setIsOpen(false);
        setSubMenuDashboardId(null);
        setSubMenuPosition(null);
      }
    },
    [isOpen],
  );

  const deleteDashboard = useCallback(
    (event: React.MouseEvent, dash: Dashboard) =>
    {
      functions?.deleteDashboard?.(event, dash);
      setOpenDeleteTabModal(true);
      setCurrentDashboard(dash);
      closeMenu();
    },
    [functions, closeMenu],
  );

  const shareDashboard = useCallback(
    async (event: React.MouseEvent, dash: Dashboard) =>
    {
      const url = getUrl(dash);
      try
      {
        await navigator.clipboard.writeText(url);
        notifyLinkCopied();
      }
      catch (_err)
      {
        try
        {
          const textArea = document.createElement('textarea');
          textArea.value = url;
          textArea.style.position = 'fixed';
          textArea.style.left = '-9999px';
          textArea.style.top = '-9999px';
          document.body.appendChild(textArea);
          textArea.focus();
          textArea.select();
          const successful = document.execCommand('copy');
          document.body.removeChild(textArea);
          if (successful)
          {
            notifyLinkCopied();
          }
        }
        catch (fallbackErr)
        {
          console.error('Failed to copy to clipboard:', fallbackErr);
        }
      }
      functions?.shareDashboard?.(event, dash);
      closeMenu();
    },
    [functions, getUrl, closeMenu, notifyLinkCopied],
  );

  const duplicateDashboard = useCallback(
    (event: React.MouseEvent, dash: Dashboard) =>
    {
      functions?.duplicateDashboard?.(event, dash);
      setDuplicateModal(true);
      setCurrentDashboard(dash);
      closeMenu();
    },
    [functions, closeMenu],
  );

  const editDashboard = useCallback(
    (_event: React.MouseEvent, dash: Dashboard) =>
    {
      // functions?.editDashboard?.(_event, dash);
      setCurrentDashboard(dash);
      setOpenEditModal(true);
      closeMenu();
    },
    [functions, closeMenu],
  );

  const openEditDashboard = useCallback(
    (event: React.MouseEvent, dash: Dashboard) =>
    {
      functions?.openEditDashboard?.(event, dash, true);
      closeMenu();
    },
    [functions, closeMenu],
  );

  const editSPPeople = useCallback(
    (event: React.MouseEvent, dash: Dashboard) =>
    {
      functions?.editSPPeople?.(event, dash);
      setOpenConfigSpModal(true);
      setCurrentDashboard(dash);
      closeMenu();
    },
    [functions, closeMenu],
  );

  const configCapacityTracker = useCallback(
    (event: React.MouseEvent, dash: Dashboard) =>
    {
      functions?.editCapacityTracker?.(event, dash);
      setOpenCapacityTracker(true);
      setCurrentDashboard(dash);
      closeMenu();
    },
    [functions, closeMenu],
  );

  const exportEfficiency = useCallback(
    async (_event: React.MouseEvent, dash: Dashboard) =>
    {
      if (dash.StartDate === null || dash.DueDate === null)
      {
        setShowConfirmationModal(true);
        setIsOpen(false);
        setCurrentDashboard(dash);
        return;
      }

      const requestObj = {
        DashboardId: dash.DashboardId,
        StartDate: dash.StartDate,
        DueDate: dash.DueDate,
        Today: getDateActual(),
        Name: dash.Name,
        WebUrl: config.webUrl || '',
      };

      functions?.loadingBarStart?.();

      try
      {
        const blob = await dashboardApi.reportDashboardEfficiency(requestObj as any);

        if (!blob || blob.size < 3)
        {
          functions?.loadingBarComplete?.();
          // Show notification
          const noContentEl = document.getElementById('export-no-content');
          if (noContentEl)
          {
            noContentEl.style.display = 'block';
            setTimeout(() =>
            {
              noContentEl.style.display = 'none';
            }, 4000);
          }
          return;
        }

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${requestObj.Name}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        functions?.loadingBarComplete?.();
      }
      catch (error)
      {
        functions?.loadingBarComplete?.();
        alert('An error has occurred, try again later');
      }

      closeMenu();
    },
    [config, functions, getDateActual, closeMenu],
  );

  // const _archiveDashboardGroup = ... // unused

  const showReports = useCallback(
    (event: React.MouseEvent, dash: Dashboard) =>
    {
      functions?.showReports?.(event, dash);
      closeMenu();
    },
    [functions, closeMenu],
  );

  const pinTabDashboard = useCallback(
    async (event: React.MouseEvent, dash: Dashboard, show: boolean) =>
    {
      functions?.pinTabDashboard?.(event, dash);
      void (dashboards ?? []);
      let pin: Dashboard[] = [];
      let notpin: Dashboard[] = [];
      if (show)
      {
        // show=true means HIDE the dashboard
        const socketId = config.functions?.getSocketConnectionId?.() || '';
        await dashboardService.DeletePinedTabDashboard(
          dash.ConfigValueId || 1,
          dash.DashboardId,
          dash.ProjectId || 0,
          socketId,
        );
        // Use functional setters to always operate on the latest state, avoiding
        // stale-closure issues with notPinedDashboards (not passed as prop for typeMenu="1")
        const hiddenDash = {
          ...dash,
          IsTabPinedDashboard: false,
          IsOpenDashboard: false,
          Order: 0,
        };
        let remainingPin: Dashboard[] = [];
        setPinedDashboards(prev =>
        {
          remainingPin = prev.filter(d => d.DashboardId !== dash.DashboardId);
          return remainingPin;
        });
        setNotPinDashboard(prev => [...prev, hiddenDash]);
        // If the hidden dashboard was the active one, switch to the first remaining pinned tab
        if (activeDashboard === dash.DashboardId)
        {
          setTimeout(() =>
          {
            if (remainingPin.length > 0)
            {
              onToggleTab(event, remainingPin[0]!.DashboardId, remainingPin[0]!);
            }
          });
        }
      }
      else
      {
        const currentPinned = tabsToShow || [];
        const lastOrder = Math.max(...currentPinned.map(o => o.Order ?? 0), 0);
        const newOrder = lastOrder + 1;
        const socketId = config.functions?.getSocketConnectionId?.() || '';
        await dashboardService.AddPinedTabDashboard(
          dash.DashboardId,
          newOrder,
          dash.ConfigValueId || 1,
          dash.ProjectId || 0,
          true,
          socketId,
        );
        // Build pin from current pinned dashboards (accurate after prior hides),
        // then push the shown dashboard at the END so it appears last in the tab bar
        pin = [...currentPinned];
        notpin = notPinedDashboards.filter(d => d.DashboardId !== dash.DashboardId);
        const shownDash = {
          ...dash,
          IsTabPinedDashboard: true,
          IsOpenDashboard: false,
          Order: newOrder,
        };
        pin.push(shownDash);
        setPinedDashboards(pin);
        setNotPinDashboard(notpin);
        onSelectDashboard?.(shownDash);
      }

      closeMenu();
    },
    [functions, closeMenu, activeDashboard, dashboards, onToggleTab, onSelectDashboard],
  );

  const OpenTabDashboard = useCallback(
    (event: React.MouseEvent, dash: Dashboard) =>
    {
      openTabDashboard?.(event, dash);
      closeMenu();
    },
    [openTabDashboard, closeMenu],
  );

  const filterList = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) =>
    {
      const text = event.target.value.toLowerCase();
      const filtered = (initialNotPinedDashboards || []).filter(x =>
        x.Name.toLowerCase().includes(text),
      );
      setNotPinedDashboardsFiltered(filtered);
    },
    [initialNotPinedDashboards],
  );

  const openSubMenu = useCallback(
    (event: React.MouseEvent, dash: Dashboard) =>
    {
      // Capture rect synchronously before event.currentTarget becomes null
      const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();

      const isSameItem = subMenuDashboardId === dash.DashboardId;

      // Same item toggles closed
      if (isSameItem)
      {
        setSubMenuDashboardId(null);
        setSubMenuPosition(null);
        return;
      }

      // Mount hidden, then measure and place
      setSubMenuDashboardId(dash.DashboardId);
      setSubMenuPosition(null);

      requestAnimationFrame(() =>
      {
        const menu = subMenuRef.current;
        if (!menu) return;

        const menuWidth = menu.offsetWidth;
        let leftPos = rect.right + 16;
        if (leftPos + menuWidth >= window.innerWidth)
        {
          leftPos = rect.left - menuWidth - 3;
        }

        setSubMenuPosition({ top: rect.top, left: leftPos });
      });
    },
    [subMenuDashboardId],
  );

  const handleJiraModal = (_event: React.MouseEvent, dash: Dashboard) =>
  {
    setImportJiraModalOpen(true);
    setIsOpen(false);
    setImportJiraSelectedDashboard(dash);
  };

  const handleCloseJiraModal = () =>
  {
    setImportJiraModalOpen(false);
    setIsOpen(false);
    setImportJiraSelectedDashboard(null);
  };

  const handleConfigurationModal = useCallback(
    (_event: React.MouseEvent, dash: Dashboard) =>
    {
      const configData = {
        api: config.api,
        projectId: dash.ProjectId,
        dashboardId: dash.DashboardId,
        setDashboard: functions?.setDashboard,
        item: dash,
      };
      setCurrentDashboard(dash);
      setConfigDashboard(configData);
      setEditProjectColumnModalOpen(true);
      setIsOpen(false);
    },
    [config, functions],
  );

  // const _handleCloseConfigurationModal = ... // unused

  const renderActionMenu = (dash: Dashboard) =>
  {
    if (dash.FakeDashboardType !== 0 && dash.FakeDashboardType !== 4)
    {
      return null;
    }

    return (
      <ul
        ref={menuRef}
        className="corgee_com_card-action-header-menu-tabs dropdown-menu cancel-drag"
        style={{
          left: menuPosition.left,
          top: menuPosition.top,
          ...(menuMaxHeight !== undefined && { maxHeight: `${menuMaxHeight}px` }),
        }}
      >
        {!dash.IsDefault && dash.showDeleteDashboard && (
          <li role="menuitem" className="corgee_com_card-action-menu-item-tabs">
            <button
              className="corgee_com_card-action-menu-item-tabs-button"
              type="button"
              onClick={e => deleteDashboard(e, dash)}
            >
              <i className="icon-trash-icon"></i>
              <span>Delete Dashboard</span>
            </button>
          </li>
        )}

        {dash.FakeDashboardType === 0 && (
          <li role="menuitem" className="corgee_com_card-action-menu-item-tabs">
            <button
              className="copy-to-clipboard corgee_com_card-action-menu-item-tabs-button"
              type="button"
              data-clipboard-text={getUrl(dash)}
              onClick={e => shareDashboard(e, dash)}
            >
              <i className="fa fa-share"></i>
              <span>Copy Link To Clipboard</span>
            </button>
          </li>
        )}

        {(!dash.IsDefault || !dash.ProjectId) && canEdit && config.isProjectDetail && (
          <li role="menuitem" className="corgee_com_card-action-menu-item-tabs">
            <button
              className="configure-columns corgee_com_card-action-menu-item-tabs-button"
              type="button"
              onClick={e => handleConfigurationModal(e, dash)}
            >
              <i className="fa fa-sliders"></i>
              <span>Edit Dashboard Column</span>
            </button>
          </li>
        )}

        {(isAdmin || canEdit) && dash.FakeDashboardType === 0 && (
          <li role="menuitem" className="corgee_com_card-action-menu-item-tabs">
            <button
              className="corgee_com_card-action-menu-item-tabs-button"
              type="button"
              onClick={e => duplicateDashboard(e, dash)}
            >
              <i className="fa fa-clone"></i>
              <span>Duplicate</span>
            </button>
          </li>
        )}

        {/* Always visible; name editability is gated inside the modal via canEdit */}
        <li role="menuitem" className="corgee_com_card-action-menu-item-tabs">
          <button
            className="corgee_com_card-action-menu-item-tabs-button"
            type="button"
            onClick={e => editDashboard(e, dash)}
          >
            <i className="icon-pencil-icon"></i>
            <span>Edit Dashboard</span>
          </button>
        </li>

        {(!dash.IsDefault || !dash.ProjectId) && canEdit && (
          <li role="menuitem" className="corgee_com_card-action-menu-item-tabs">
            <button
              className="corgee_com_card-action-menu-item-tabs-button"
              type="button"
              onClick={e => exportEfficiency(e, dash)}
            >
              <i className="fa fa-file-text"></i>
              <span>Export Efficiency</span>
            </button>
          </li>
        )}

        <li role="menuitem" className="corgee_com_card-action-menu-item-tabs">
          <button
            className="corgee_com_card-action-menu-item-tabs-button"
            type="button"
            onClick={e => showReports(e, dash)}
          >
            <i className="fa fa-bar-chart"></i>
            <span>{dash.ReportId && dash.ReportId > 0 ? 'View Report' : 'Add Report'}</span>
          </button>
        </li>

        {(dash.IsTabPinedDashboard || dash.IsPinedDashboard) &&
          !dash.IsOpenDashboard &&
          dash.FakeDashboardType !== 4 && (
          <li role="menuitem" className="corgee_com_card-action-menu-item-tabs">
            <button
              className="corgee_com_card-action-menu-item-tabs-button"
              type="button"
              onClick={e => pinTabDashboard(e, dash, true)}
            >
              <i className="fa fa-eye-slash"></i>
              <span>Hide Dashboard</span>
            </button>
          </li>
        )}

        {dash.IsOpenDashboard && dash.FakeDashboardType !== 4 && (
          <li role="menuitem" className="corgee_com_card-action-menu-item-tabs">
            <button
              className="corgee_com_card-action-menu-item-tabs-button"
              type="button"
              onClick={e => pinTabDashboard(e, dash, false)}
            >
              <i className="fa fa-eye"></i>
              <span>Show Dashboard</span>
            </button>
          </li>
        )}

        {isPTOApprover && dash.FakeDashboardType === 0 && dash.ProjectId && (
          <li role="menuitem" className="corgee_com_card-action-menu-item-tabs">
            <button
              className="corgee_com_card-action-menu-item-tabs-button"
              type="button"
              onClick={e => handleJiraModal(e, dash)}
            >
              <i className="fa fa-files-o"></i>
              <span>Import from Jira</span>
            </button>
          </li>
        )}

        {(!dash.IsDefault || !dash.ProjectId) &&
          (isAdmin || isPTOApprover) &&
          config.isProjectDetail && (
          <li role="menuitem" className="corgee_com_card-action-menu-item-tabs">
            <button
              className="corgee_com_card-action-menu-item-tabs-button"
              type="button"
              onClick={e => editSPPeople(e, dash)}
            >
              <i className="fa fa-users"></i>
              <span>Config SP by Person</span>
            </button>
          </li>
        )}

        {(!dash.IsDefault || !dash.ProjectId) &&
          (isAdmin || isPTOApprover) &&
          config.isProjectDetail && (
          <li role="menuitem" className="corgee_com_card-action-menu-item-tabs">
            <button
              className="corgee_com_card-action-menu-item-tabs-button"
              type="button"
              onClick={e => configCapacityTracker(e, dash)}
            >
              <i className="fa fa-users"></i>
              <span>Config Capacity Tracker</span>
            </button>
          </li>
        )}
      </ul>
    );
  };

  // Single portaled submenu. RenderInBody defers unmount, which nulled the
  // shared ref and left the incoming submenu hidden.
  const renderSubMenu = () =>
  {
    const dash =
      subMenuDashboardId === null
        ? null
        : notPinedDashboardsFiltered.find(d => d.DashboardId === subMenuDashboardId) ??
          notPinedDashboards.find(d => d.DashboardId === subMenuDashboardId) ??
          null;
    if (!dash || (dash.FakeDashboardType !== 0 && dash.FakeDashboardType !== 4))
    {
      return null;
    }

    return createPortal(
      <ul
        className="corgee_com_card-action-submenu-tabs dropdown-menu"
        ref={subMenuRef}
        tabIndex={0}
        style={{
          top: subMenuPosition?.top ?? 0,
          left: subMenuPosition?.left ?? 0,
          visibility: subMenuPosition ? 'visible' : 'hidden',
        }}
      >
        {!dash.IsDefault && dash.showDeleteDashboard && (
          <li role="menuitem" className="corgee_com_card-action-menu-item-tabs">
            <button
              className="corgee_com_card-action-menu-item-tabs-button"
              type="button"
              onClick={e => deleteDashboard(e, dash)}
            >
              <i className="icon-trash-icon"></i>
              <span>Delete Dashboard</span>
            </button>
          </li>
        )}

        {dash.FakeDashboardType === 0 && (
          <li role="menuitem" className="corgee_com_card-action-menu-item-tabs">
            <button
              className="copy-to-clipboard corgee_com_card-action-menu-item-tabs-button"
              type="button"
              data-clipboard-text={getUrl(dash)}
              onClick={e => shareDashboard(e, dash)}
            >
              <i className="fa fa-share"></i>
              <span>Copy Link To Clipboard</span>
            </button>
          </li>
        )}

        {(!dash.IsDefault || !dash.ProjectId) && canEdit && config.isProjectDetail && (
          <li role="menuitem" className="corgee_com_card-action-menu-item-tabs">
            <button
              className="configure-columns corgee_com_card-action-menu-item-tabs-button"
              type="button"
              onClick={e => handleConfigurationModal(e, dash)}
            >
              <i className="fa fa-sliders"></i>
              <span>Edit Dashboard Column</span>
            </button>
          </li>
        )}

        {(isAdmin || canEdit) && dash.FakeDashboardType === 0 && (
          <li role="menuitem" className="corgee_com_card-action-menu-item-tabs">
            <button
              className="corgee_com_card-action-menu-item-tabs-button"
              type="button"
              onClick={e => duplicateDashboard(e, dash)}
            >
              <i className="fa fa-clone"></i>
              <span>Duplicate</span>
            </button>
          </li>
        )}

        {/* Always visible; name editability is gated inside the modal via canEdit */}
        <li role="menuitem" className="corgee_com_card-action-menu-item-tabs">
          <button
            className="corgee_com_card-action-menu-item-tabs-button"
            type="button"
            onClick={e => editDashboard(e, dash)}
          >
            <i className="icon-pencil-icon"></i>
            <span>Edit Dashboard</span>
          </button>
        </li>

        {(!dash.IsDefault || !dash.ProjectId) && canEdit && (
          <li role="menuitem" className="corgee_com_card-action-menu-item-tabs">
            <button
              className="corgee_com_card-action-menu-item-tabs-button"
              type="button"
              onClick={e => exportEfficiency(e, dash)}
            >
              <i className="fa fa-file-text"></i>
              <span>Export Efficiency</span>
            </button>
          </li>
        )}

        <li role="menuitem" className="corgee_com_card-action-menu-item-tabs">
          <button
            className="corgee_com_card-action-menu-item-tabs-button"
            type="button"
            onClick={e => showReports(e, dash)}
          >
            <i className="fa fa-bar-chart"></i>
            <span>{dash.ReportId && dash.ReportId > 0 ? 'View Report' : 'Add Report'}</span>
          </button>
        </li>

        {dash.IsTabPinedDashboard && !dash.IsOpenDashboard && dash.FakeDashboardType !== 4 && (
          <li role="menuitem" className="corgee_com_card-action-menu-item-tabs">
            <button
              className="corgee_com_card-action-menu-item-tabs-button"
              type="button"
              onClick={e => pinTabDashboard(e, dash, true)}
            >
              <i className="fa fa-eye-slash"></i>
              <span>Hide Dashboard</span>
            </button>
          </li>
        )}

        {dash.FakeDashboardType !== 4 && (
          <li role="menuitem" className="corgee_com_card-action-menu-item-tabs">
            <button
              className="corgee_com_card-action-menu-item-tabs-button"
              type="button"
              onClick={e => pinTabDashboard(e, dash, false)}
            >
              <i className="fa fa-eye"></i>
              <span>Show Dashboard</span>
            </button>
          </li>
        )}

        {dash.FakeDashboardType !== 4 && (
          <li role="menuitem" className="corgee_com_card-action-menu-item-tabs">
            <button
              className="corgee_com_card-action-menu-item-tabs-button"
              type="button"
              onClick={e => OpenTabDashboard(e, dash)}
            >
              <i className="fa fa-folder-open-o"></i>
              <span>Open Dashboard</span>
            </button>
          </li>
        )}

        {(!dash.IsDefault || !dash.ProjectId) &&
          (isAdmin || isPTOApprover) &&
          config.isProjectDetail && (
          <li role="menuitem" className="corgee_com_card-action-menu-item-tabs">
            <button
              className="corgee_com_card-action-menu-item-tabs-button"
              type="button"
              onClick={e => editSPPeople(e, dash)}
            >
              <i className="fa fa-users"></i>
              <span>Config SP by Person</span>
            </button>
          </li>
        )}

        {(!dash.IsDefault || !dash.ProjectId) &&
          (isAdmin || isPTOApprover) &&
          config.isProjectDetail && (
          <li role="menuitem" className="corgee_com_card-action-menu-item-tabs">
            <button
              className="corgee_com_card-action-menu-item-tabs-button"
              type="button"
              onClick={e => configCapacityTracker(e, dash)}
            >
              <i className="fa fa-users"></i>
              <span>Config Capacity Tracker</span>
            </button>
          </li>
        )}
      </ul>,
      document.body,
    );
  };

  const renderMoreMenu = () =>
  {
    const notPined = notPinedDashboardsFiltered.filter(x => x.FakeDashboardType !== 1);

    return (
      <div>
        <ul
          ref={menuRef}
          className="corgee_com_card-action-header-menu-tabs dropdown-menu cancel-drag"
          style={{
            right: menuPosition.right,
            left: 'auto',
            top: menuPosition.top,
            ...(menuMaxHeight !== undefined && { maxHeight: `${menuMaxHeight}px` }),
          }}
        >
          <input
            type="text"
            className="sidebar-filter-input-tabs"
            placeholder="Filter a dashboard"
            onChange={filterList}
          />
          {notPined.map((dash, index) =>
          {
            return (
              <li
                key={index}
                role="menuitem"
                className="corgee_com_card-action-menu-item-tabs dropdown-submenu"
              >
                <button
                  className="corgee_com_card-action-menu-item-tabs-button"
                  type="button"
                  onClick={e => openSubMenu(e, dash)}
                >
                  {(dash.IsPineableDashboard || dash.IsPinedDashboard) && (
                    <i
                      className={
                        dash.IsPineableDashboard
                          ? 'fa fa-thumb-tack io-separate-tabs unpinned'
                          : 'fa fa-thumb-tack io-separate-tabs'
                      }
                    ></i>
                  )}
                  <span>{dash.Name}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    );
  };

  const notPined = notPinedDashboards.filter(x => x.FakeDashboardType !== 1);

  return (
    <div className="threepoints-tabs">
      <div
        id="actionMenuElement"
        ref={actionMenuRef}
        tabIndex={0}
        className="corgee_com_card-action-menu-group-tabs dropdown open"
      >
        {typeMenu === '1' && (
          <button
            className="corgee_com_card-action-menu-toggle-tabs dropdown-toggle"
            data-toggle="dropdown"
            onClick={onOpenMenu}
          >
            <img
              src={threeDotsIcon}
              className="corgee_com_card-action-menu-icon-tabs three-dots-icon"
              alt=""
            />
          </button>
        )}

        {typeMenu === '2' && notPined.length > 0 && (
          <div className="div-more-tabs" onClick={onOpenMenu}>
            <span className="more-button">More</span>
            <span className="fa fa-caret-down"></span>
          </div>
        )}

        {isOpen && (
          <div>
            <RenderInBody>
              <div>
                {typeMenu === '1' && renderActionMenu(dashboard)}
                {typeMenu === '2' && notPined.length > 0 && renderMoreMenu()}
              </div>
            </RenderInBody>
          </div>
        )}
      </div>

      {isOpen && typeMenu === '2' && renderSubMenu()}

      {isOpen && (
        <RenderInBody>
          <div className="corgee_com_card-action-menu-backdrop-tabs" onClick={closeMenu}></div>
        </RenderInBody>
      )}

      {showConfirmationModal && (
        <Modal open={showConfirmationModal} onClose={() => closeMenu()} title="Export Efficiency">
          <div>
            <p className="pt-[10px] pb-[8px] text-sm">
              Please make sure to update the dashboard's dates and recalculate the dashboard before
              exporting.
            </p>
            <div className="pt-3 text-right gap-[10px] relative overflow-hidden min-h-6 flex flex-row-reverse">
              <button
                type="button"
                onClick={e => currentDashboard && openEditDashboard(e, currentDashboard)}
                className="bg-corgee-blue hover:bg-corgee-BlueHover border text-white text-xs h-7 px-3 py-1"
              >
                Continue
              </button>
              <button
                type="button"
                onClick={() => closeMenu()}
                className="bg-brightGray hover:bg-corgee-brightGrayHover border text-white text-xs h-7 px-3 py-1"
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}
      {importJiraModalOpen && (
        <RenderInBody>
          <ImportFromJira
            open={importJiraModalOpen}
            closeModal={handleCloseJiraModal}
            config={config}
            selectedDashboard={importJiraSelectedDashboard}
            ifjMoveToModal={ifjMoveToModal as any}
          />
        </RenderInBody>
      )}
      {openEditModal && (
        <RenderInBody>
          <EditDashboardModal
            dashboard={currentDashboard}
            canEdit={Boolean(currentDashboard?.IsDashboardCreator) || isAdmin}
            open={openEditModal}
            setOpen={setOpenEditModal}
            onSave={(updated: Dashboard) =>
            {
              setPinedDashboards(prev =>
                prev.map(t =>
                  t.DashboardId === updated.DashboardId
                    ? { ...t, Name: updated.Name, IsPublic: updated.IsPublic }
                    : t,
                ),
              );
              onDashboardUpdated?.(updated);
              toastSuccess('Dashboard saved successfully');
            }}
          />
        </RenderInBody>
      )}
      {openConfigSpModal && (
        <RenderInBody>
          <ConfigSpModal
            dashboard={currentDashboard}
            open={openConfigSpModal}
            setOpen={setOpenConfigSpModal}
          />
        </RenderInBody>
      )}
      {openCapacityTracker && (
        <RenderInBody>
          <ConfigCapacityTracker
            dashboard={currentDashboard}
            open={openCapacityTracker}
            setOpen={setOpenCapacityTracker}
          />
        </RenderInBody>
      )}
      {openDeleteTabModal && (
        <RenderInBody>
          <DeleteDashboardModal
            dashboard={currentDashboard}
            open={openDeleteTabModal}
            setOpen={setOpenDeleteTabModal}
            activeDashboard={activeDashboard}
            dashboards={tabsToShow || []}
            setTabsToShow={setPinedDashboards}
            selectDashboard={onSelectDashboard}
          />
        </RenderInBody>
      )}
      {configDashboard && (
        <RenderInBody>
          <EditDashboardColumn
            dashboard={currentDashboard as any}
            open={configDashboard}
            setOpen={setConfigDashboard}
            typeConfiguration="Dashboard"
            reloadDashboard={handleReloadDashboard}
          />
        </RenderInBody>
      )}
      {duplicateModal && (
        <RenderInBody>
          <DuplicateModal
            dashboard={currentDashboard}
            open={duplicateModal}
            setOpen={setDuplicateModal}
            isProjectDetail={config.isProjectDetail}
            onDuplicated={(newDashboardId: number) => functions?.onDuplicated?.(newDashboardId)}
          />
        </RenderInBody>
      )}

      {showCopyNotification &&
        createPortal(
          <div
            className="corgee_com_notification-bubble-container"
            style={{
              display: 'block',
              position: 'fixed',
              top: '9rem',
              zIndex: 9999,
              opacity: copyNotificationFading ? 0 : 1,
              transition: 'opacity 300ms ease-out',
            }}
          >
            <div className="corgee_com_notification-bubble">
              Link has been copied to clipboard
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default ActionMenuTab;
